import {
  CheckCircleFilled,
  CloseCircleFilled,
  ExclamationCircleFilled,
  InfoCircleFilled,
  QuestionCircleFilled,
} from '@ant-design/icons'
import { Modal } from 'antd'
import classNames from 'classnames'
import type { ReactNode } from 'react'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { useUserInfoStore } from '@/stores/user/userInfoStore'

import cn from './AtomModal.module.scss'

const DEFAULT_WIDTH = 460

export type AtomModalType = 'confirm' | 'info' | 'success' | 'warning' | 'error'

const TYPE_ICONS: Record<AtomModalType, ReactNode> = {
  confirm: <QuestionCircleFilled />,
  info: <InfoCircleFilled />,
  success: <CheckCircleFilled />,
  warning: <ExclamationCircleFilled />,
  error: <CloseCircleFilled />,
}

const TYPE_CLASS_NAMES: Record<AtomModalType, string | undefined> = {
  confirm: undefined,
  info: undefined,
  success: cn.success,
  warning: cn.warning,
  error: cn.error,
}

export type AtomModalProps = {
  open: boolean
  title: ReactNode
  onOk: () => void
  type?: AtomModalType
  children?: ReactNode
  onCancel?: () => void
  afterClose?: () => void
  okText?: string
  cancelText?: string
  danger?: boolean
  confirmLoading?: boolean
  width?: number | string
}

function AtomModal({
  open,
  title,
  onOk,
  type = 'info',
  children,
  onCancel,
  afterClose,
  okText = type === 'confirm' ? 'Подтвердить' : 'Понятно',
  cancelText = 'Отмена',
  danger = false,
  confirmLoading = false,
  width = DEFAULT_WIDTH,
}: AtomModalProps) {
  const isOrganisationSelected = useUserInfoStore(
    (state) => Object.keys(state.selectedOrganisation).length > 0
  )
  const isConfirm = type === 'confirm'
  const close = onCancel ?? onOk

  return (
    <Modal
      centered
      destroyOnHidden
      open={open}
      width={width}
      footer={null}
      closable={false}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
      onCancel={confirmLoading ? undefined : close}
      afterClose={afterClose}
      className={classNames(
        cn.modal,
        TYPE_CLASS_NAMES[type],
        danger && cn.danger,
        isOrganisationSelected && cn.organisationSelected
      )}
    >
      <div className={cn.body}>
        <span className={cn.icon} aria-hidden>
          {danger ? <ExclamationCircleFilled /> : TYPE_ICONS[type]}
        </span>
        <div className={cn.text}>
          <h2 className={cn.title}>{title}</h2>
          {children && <div className={cn.content}>{children}</div>}
        </div>
      </div>
      <div className={classNames(cn.footer, isConfirm && cn.footerConfirm)}>
        {isConfirm && (
          <AtomButton
            variant="secondary"
            disabled={confirmLoading}
            onClick={close}
          >
            {cancelText}
          </AtomButton>
        )}
        <AtomButton
          variant={danger ? 'danger' : 'primary'}
          loading={confirmLoading}
          onClick={onOk}
        >
          {okText}
        </AtomButton>
      </div>
    </Modal>
  )
}

export default AtomModal
